import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Home, ScanLine, Trophy, User, LogOut, LayoutDashboard, MapPin, Users, QrCode, BarChart3, Inbox, Wallet } from "lucide-react";

export function MobileShell({ children }) {
  return (
    <div className="nod-mobile nod-grain bg-[#0A0A0C] text-[#F8F8F2] pb-24">
      {children}
    </div>
  );
}

export function CustomerBottomNav() {
  const { pathname } = useLocation();
  const items = [
    { to: "/app", icon: Home, label: "Start", testid: "nav-customer-home" },
    { to: "/scan", icon: ScanLine, label: "Scan", testid: "nav-customer-scan" },
    { to: "/leaderboard", icon: Trophy, label: "Top", testid: "nav-customer-leaderboard" },
    { to: "/me", icon: User, label: "Ich", testid: "nav-customer-me" },
  ];
  return (
    <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-[#0A0A0C]/90 backdrop-blur-xl border-t border-white/10 px-4 pt-2 pb-3 z-50" data-testid="customer-bottom-nav">
      <ul className="flex justify-around items-center">
        {items.map(({ to, icon: Icon, label, testid }) => {
          const active = pathname === to || (to !== "/app" && pathname.startsWith(to));
          return (
            <li key={to}>
              <Link
                to={to}
                data-testid={testid}
                className={`flex flex-col items-center gap-1 px-3 py-1 transition-colors ${active ? "text-[#EBB300]" : "text-[#A1A1AA] hover:text-white"}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.75} />
                <span className="text-[10px] tracking-[0.15em] uppercase">{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function DashboardShell({ role, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const adminLinks = [
    { to: "/admin", icon: LayoutDashboard, label: "Übersicht", testid: "nav-admin-home" },
    { to: "/admin/locations", icon: MapPin, label: "Locations", testid: "nav-admin-locations" },
    { to: "/admin/staff", icon: Users, label: "Mitarbeiter", testid: "nav-admin-staff" },
    { to: "/admin/qr", icon: QrCode, label: "QR-Codes", testid: "nav-admin-qr" },
    { to: "/admin/stats", icon: BarChart3, label: "Statistiken", testid: "nav-admin-stats" },
  ];

  const staffLinks = [
    { to: "/staff", icon: Wallet, label: "Wallet", testid: "nav-staff-home" },
    { to: "/staff/inbox", icon: Inbox, label: "Posteingang", testid: "nav-staff-inbox" },
    { to: "/staff/profile", icon: User, label: "Profil", testid: "nav-staff-profile" },
    { to: "/leaderboard", icon: Trophy, label: "Leaderboard", testid: "nav-staff-leaderboard" },
  ];

  const links = role === "admin" ? adminLinks : staffLinks;

  return (
    <div className="min-h-screen flex bg-[#0A0A0C] text-[#F8F8F2]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-[#0d0d11] border-r border-white/5 p-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-baseline gap-2 mb-12" data-testid="dashboard-logo">
          <span className="text-3xl font-black tracking-tight">Nod</span>
          <span className="w-2 h-2 rounded-full bg-[#EBB300]"></span>
        </Link>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#52525B] mb-3">{role === "admin" ? "Administration" : "Service"}</div>
        <nav className="flex-1 space-y-1">
          {links.map(({ to, icon: Icon, label, testid }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#EBB300]/10 text-[#EBB300] border-l-2 border-[#EBB300]"
                    : "text-[#A1A1AA] hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-white/5 pt-4 mt-4">
          <div className="text-xs text-[#A1A1AA] mb-2 truncate" data-testid="user-display-name">{user?.name}</div>
          <div className="text-[10px] text-[#52525B] mb-3 uppercase tracking-wider">{user?.role}</div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#E05A33] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile topbar */}
        <header className="md:hidden flex items-center justify-between px-5 py-4 border-b border-white/5 bg-[#0d0d11]">
          <Link to="/" className="text-xl font-black tracking-tight">Nod<span className="text-[#EBB300]">.</span></Link>
          <button onClick={() => { logout(); navigate("/"); }} className="text-xs text-[#A1A1AA] flex items-center gap-1" data-testid="logout-button-mobile">
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </header>

        <main className="flex-1 p-5 md:p-8 pb-24 md:pb-8" data-testid="dashboard-main-content">{children}</main>

        {/* Mobile bottom nav for dashboards */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0A0A0C]/90 backdrop-blur-xl border-t border-white/10 px-3 py-2 z-50">
          <ul className="flex justify-around">
            {links.map(({ to, icon: Icon, label, testid }) => {
              const active = pathname === to;
              return (
                <li key={to}>
                  <Link to={to} data-testid={`${testid}-mobile`} className={`flex flex-col items-center px-2 py-1 ${active ? "text-[#EBB300]" : "text-[#A1A1AA]"}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                    <span className="text-[9px] tracking-[0.15em] uppercase mt-1">{label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
